import Cookies from "js-cookie";
import { getAnalyticsUserId } from "./analyticsIdentity";
import { getAuthHeaders, getJwt } from "./authHeaders";
import { trackFriendJoinedFromInvite } from "./frontendTrackingEvents";

const STORAGE_KEY = "c8_squares_pending_link_invite";

export type SquaresLinkPendingInvite = {
    matchId: string;
    inviterPlayerId: string;
};

/** Decode `back=/squares?match=&invitedBy=` from login/signup query and persist invite. */
export function persistSquaresLinkInviteFromBackParam(back: string | null | undefined): void {
    if (!back || typeof back !== "string") return;
    const trimmed = back.trim();
    if (!trimmed) return;
    const withSlash = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    if (!withSlash.startsWith("/squares")) return;
    const qIndex = withSlash.indexOf("?");
    if (qIndex === -1) return;
    persistSquaresLinkInviteFromSearch(withSlash.slice(qIndex));
}

/** If URL has match + invitedBy, remember for post-login claim (e.g. new signup). */
export function persistSquaresLinkInviteFromSearch(search: string): void {
    try {
        const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
        const match = params.get("match");
        const invitedBy = params.get("invitedBy") || params.get("invited_by");
        if (!match?.trim() || !invitedBy?.trim()) return;
        const payload: SquaresLinkPendingInvite = {
            matchId: match.trim(),
            inviterPlayerId: invitedBy.trim(),
        };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        /* ignore */
    }
}

/** Current page `?search` plus optional `back=` pointing at a Squares invite link. */
export function persistSquaresInviteFromRouterSearch(search: string): void {
    persistSquaresLinkInviteFromSearch(search);
    try {
        const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
        persistSquaresLinkInviteFromBackParam(params.get("back"));
    } catch {
        /* ignore */
    }
}

/** Drop pending Squares invite (e.g. user opened `/bullion?match=&invitedBy=` — same params as Squares). */
export function clearSquaresLinkInvitePendingFromStorage(): void {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

function readPending(): SquaresLinkPendingInvite | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const o = JSON.parse(raw) as Partial<SquaresLinkPendingInvite>;
        if (!o?.matchId || !o?.inviterPlayerId) return null;
        return { matchId: String(o.matchId).trim(), inviterPlayerId: String(o.inviterPlayerId).trim() };
    } catch {
        return null;
    }
}

/**
 * After login, create squares_match_challenge from a shared Play-with-a-friend link (if pending).
 * Clears storage only on success so transient failures retry.
 * @param analyticsUserIdHint — logged-in user id for analytics (falls back to in-memory analytics id).
 */
export async function tryClaimSquaresLinkInviteFromStorage(
    analyticsUserIdHint?: string | number | null
): Promise<void> {
    const pending = readPending();
    if (!pending) return;
    if (!Cookies.get("c8_session_token") && !getJwt()) return;
    try {
        const res = await fetch(`${process.env.API_URL}/account/squares-invite-from-link/claim`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({
                match_id: pending.matchId,
                inviter_player_id: pending.inviterPlayerId,
            }),
        });
        if (res.ok) {
            sessionStorage.removeItem(STORAGE_KEY);
            const uid = analyticsUserIdHint ?? getAnalyticsUserId();
            trackFriendJoinedFromInvite({
                user_id: uid != null && String(uid).trim() !== "" ? uid : undefined,
                invite_method: "link",
                game_type: "squares",
                visibility: "public",
                entry_point: "squares_lobby",
                fixture_id: pending.matchId,
                pathname: typeof window !== "undefined" ? window.location.pathname : "/squares",
            });
            return;
        }
        if (res.status === 400) {
            const j = (await res.json().catch(() => ({}))) as { error?: string };
            const err = j?.error;
            if (err === "match_not_eligible" || err === "inviter_not_found" || err === "self") {
                sessionStorage.removeItem(STORAGE_KEY);
            }
        }
    } catch {
        /* keep pending for retry */
    }
}
